/**
 * 对方阵营信息面板（Optical 重构）。
 * 双盲规则下，对方卡片只在配对环节结束（Reveal）后可见。
 */
import type {
  ConstructiveStrategyCard,
  CrossExTaskCard,
  DealtCard,
  Participant,
  RevealedCard,
  SandboxCard,
  Side,
} from '@zongheng/core-schema';
import { sideLabel } from '@zongheng/core-schema';
import { Badge } from '../ui/primitives.tsx';

export function OpponentPanel({
  opponent,
  opponentSide,
  revealed,
}: {
  opponent: Participant | null;
  opponentSide: Side;
  /** 当前配对中对方已揭示的卡片（未揭示时为 null） */
  revealed: RevealedCard | null;
}) {
  const isAff = opponentSide === 'AFF';
  const accent = isAff ? '#7DD3FC' : '#FDA4AF';
  return (
    <section data-testid="hud-opponent" aria-label="对方阵营" className="lens p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: accent, boxShadow: `0 0 10px ${accent}` }}
          />
          <h2 className="text-[13px] font-medium tracking-wide text-slate-100">对方阵营</h2>
        </div>
        <Badge tone={isAff ? 'aff' : 'neg'}>{sideLabel(opponentSide)}</Badge>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/[0.025] text-[13px] text-slate-300 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),inset_0_0_0_1px_rgba(255,255,255,0.04)]">
          {opponent?.side === 'AFF' ? '正' : '反'}
        </span>
        <div className="min-w-0">
          <div className="truncate text-[13px] text-slate-200">{opponent?.name ?? '等待对方加入'}</div>
          <div className="mt-0.5 text-[10px] text-slate-500">
            {opponent?.seat ? `${opponent.seat} 辩` : '辩位未知'}
            {opponent?.role === 'TEACHER' ? ' · 教师' : ''}
          </div>
        </div>
      </div>

      <div className="hairline my-3" />

      {revealed ? (
        <div data-testid="hud-opponent-revealed">
          <div className="flex items-center gap-2">
            <Badge tone="pass">已揭示</Badge>
            <span className="mono text-[10px] text-slate-500">STAGE {revealed.stageIndex}</span>
          </div>
          <p className="mt-1.5 text-[13px] font-medium text-slate-100">{cardTitle(revealed.card)}</p>
          <p className="clamp-3 mt-1 text-[11px] leading-relaxed text-slate-400">
            {cardSummary(revealed.card)}
          </p>
        </div>
      ) : (
        <div data-testid="hud-opponent-blind" className="py-1 text-center">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            className="mx-auto text-[#A5B4FC]"
            aria-hidden="true"
          >
            <rect x="4.5" y="10" width="15" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M8 10V7.5a4 4 0 0 1 8 0V10" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="12" cy="15" r="1.4" fill="currentColor" />
          </svg>
          <p className="mt-2 text-[12px] text-slate-300">对方底牌未揭示</p>
          <p className="mt-1 text-[10px] leading-relaxed text-slate-500">
            双盲协议：仅承诺哈希公开，本环节结束自动 Reveal
          </p>
        </div>
      )}
    </section>
  );
}

/** 卡片标题（供揭示摘要 / 复盘复用） */
export function cardTitle(card: DealtCard): string {
  switch (card.kind) {
    case 'CONSTRUCTIVE':
      return (card.card as ConstructiveStrategyCard).title;
    case 'CROSS_EXAM':
      return `质询任务卡 · ${(card.card as CrossExTaskCard).targetRole}`;
    case 'SANDBOX':
      return (card.card as SandboxCard).title;
    default:
      return '未知卡片';
  }
}

/** 卡片一行摘要（供揭示摘要 / 复盘复用） */
export function cardSummary(card: DealtCard): string {
  switch (card.kind) {
    case 'CONSTRUCTIVE': {
      const constructive = card.card as ConstructiveStrategyCard;
      return constructive.coreMission;
    }
    case 'CROSS_EXAM': {
      const crossEx = card.card as CrossExTaskCard;
      return crossEx.questionChain.step1LockPremise;
    }
    case 'SANDBOX': {
      const sandbox = card.card as SandboxCard;
      return sandbox.perspectives[card.perspective].label;
    }
    default:
      return '';
  }
}
